import type { CSSObject, SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import type { PaginationProps as AntdPaginationProps } from 'antd';
import { Pagination as AntdPagination } from 'antd';

import type { Theme } from '../../theme';
import { DesignSystemAntDConfigProvider } from '../DesignSystemProvider';
import { useDesignSystemTheme } from '../Hooks';
import type { DangerouslySetAntdProps, HTMLDataAttributes } from '../types';
import { importantify } from '../utils/css-utils';

export interface PaginationProps extends HTMLDataAttributes, DangerouslySetAntdProps<AntdPaginationProps> {
  /**
   * The index of the current page. Starts at 1.
   */
  currentPageIndex: number;
  /**
   * The number of results per page.
   */
  pageSize: number;
  /**
   * The total number of results across all pages.
   */
  numTotal: number;
  /**
   * Callback that is triggered when the user navigates to a different page. Recieves the index
   * of the new page and the size of that page.
   */
  onChange: (pageIndex: number, pageSize?: number) => void;
}

export function getPaginationEmotionStyles(clsPrefix: string, theme: Theme): SerializedStyles {
  const classRoot = `.${clsPrefix}-pagination`;
  const classItem = `.${clsPrefix}-pagination-item`;
  const classLink = `.${clsPrefix}-pagination-item-link`;
  const classActive = `.${clsPrefix}-pagination-item-active`;
  const classEllipsis = `.${clsPrefix}-pagination-item-ellipsis`;
  const classNext = `.${clsPrefix}-pagination-next`;
  const classPrev = `.${clsPrefix}-pagination-prev`;
  const classJumpNext = `.${clsPrefix}-pagination-jump-next`;
  const classJumpPrev = `.${clsPrefix}-pagination-jump-prev`;
  const classSizeChanger = `.${clsPrefix}-pagination-options-size-changer`;
  const classOptions = `.${clsPrefix}-pagination-options`;

  const styles: CSSObject = {
    'span[role=img]': {
      color: theme.colors.textSecondary,
      '> *': {
        color: 'inherit',
      },
    },
    [classItem]: {
      backgroundColor: 'none',
      border: 'none',
      color: theme.colors.textSecondary,
      '&:focus-visible': {
        outline: 'auto',
      },
      '> a': {
        color: theme.colors.textSecondary,
        '&:hover': {
          color: theme.colors.actionDefaultTextHover,
        },
        '&:active': {
          color: theme.colors.actionDefaultTextPress,
        },
      },
      '&:hover': {
        backgroundColor: theme.colors.actionDefaultBackgroundHover,
      },
      '&:active': {
        backgroundColor: theme.colors.actionDefaultBackgroundPress,
      },
    },
    [classActive]: {
      backgroundColor: theme.colors.actionDefaultBackgroundPress,
      color: theme.colors.actionDefaultTextPress,
      border: 'none',
      '> a': {
        color: theme.colors.actionDefaultTextPress,
      },
      '&:focus-visible': {
        outline: 'auto',
      },
      '&:hover': {
        backgroundColor: theme.colors.actionDefaultBackgroundPress,
        color: theme.colors.actionDefaultTextPress,
      },
    },
    [classLink]: {
      border: 'none',
      color: theme.colors.textSecondary,
      '&[disabled]': {
        display: 'none',
      },
      '&:hover': {
        backgroundColor: theme.colors.actionDefaultBackgroundHover,
      },
      '&:active': {
        backgroundColor: theme.colors.actionDefaultBackgroundPress,
      },
      '&:focus-visible': {
        outline: 'auto',
      },
    },
    [classEllipsis]: {
      color: 'inherit',
    },
    [`${classNext}, ${classPrev}, ${classJumpNext}, ${classJumpPrev}`]: {
      color: theme.colors.textSecondary,
      '&:hover': {
        backgroundColor: theme.colors.actionDefaultBackgroundHover,
      },
      '&:active': {
        backgroundColor: theme.colors.actionDefaultBackgroundPress,
      },
      '&:focus-visible': {
        outline: 'auto',
      },
    },
    [`&${classRoot}.mini, ${classRoot}.mini`]: {
      [`${classItem}, ${classNext}, ${classPrev}, ${classJumpNext}, ${classJumpPrev}`]: {
        height: '32px',
        width: '32px',
        lineHeight: '32px',
      },
      [classSizeChanger]: {
        marginLeft: 4,
      },
      [`input,  ${classOptions}`]: {
        height: '32px',
      },
    },
  };

  const importantStyles = importantify(styles);

  return css(importantStyles);
}

export const Pagination: React.FC<PaginationProps> = function Pagination({
  currentPageIndex,
  pageSize = 10,
  numTotal,
  onChange,
  dangerouslySetAntdProps,
}: PaginationProps): JSX.Element {
  const { classNamePrefix, theme } = useDesignSystemTheme();

  return (
    <DesignSystemAntDConfigProvider>
      <AntdPagination
        css={getPaginationEmotionStyles(classNamePrefix, theme)}
        current={currentPageIndex}
        pageSize={pageSize}
        responsive={false}
        total={numTotal}
        onChange={onChange}
        showSizeChanger={false}
        showQuickJumper={true}
        size={'small'}
        {...dangerouslySetAntdProps}
      />
    </DesignSystemAntDConfigProvider>
  );
};
